/**
 * Modal-Form zum Anlegen oder Bearbeiten einer Schicht — offline-fähig.
 *
 * Mutations gehen über die Hooks `useCreateShift` / `useUpdateShift`, die
 * optimistisch in IndexedDB schreiben und die Outbox füllen (kein Warten aufs
 * Netz — `onSaved` kommt sofort nach dem lokalen Schreiben).
 *
 * UX-Ziel: Schicht in möglichst wenigen Taps. Vorlagen sind Chips (ein Tap
 * setzt Start/Ende), Standort wird automatisch vorbelegt, und selten genutzte
 * Felder (Status, Zusatzstunden, Notiz) stecken hinter „Mehr Optionen".
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth, useCanManage } from '../store/auth';
import { useLocationsWithTemplates } from '../data/locations';
import { useCreateShift, useUpdateShift } from '../data/shifts';
import type { LocalShift, LocalTemplate } from '../db/schema';
import type { LocalLocationWithTemplates } from '../data/locations';
import type { ShiftStatus } from '../lib/shifts';
import { shiftDurationHours, shiftHours, shiftWage } from '../lib/payroll';
import { formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  /** Wenn gesetzt → Bearbeiten, sonst → Anlegen. */
  shift?: LocalShift;
  /** Vorbelegung beim Anlegen (z.B. heute oder ausgewähltes Kalender-Datum). */
  defaultDate?: string;
  onClose: () => void;
  onSaved: (shift: LocalShift) => void;
}

// Gesamtprüfung 11.09.2026: lokales Datum statt UTC (sonst nach Mitternacht bis 01/02 Uhr noch „gestern“).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ShiftFormModal({ shift, defaultDate, onClose, onSaved }: Props) {
  const me = useAuth((s) => s.user)!;
  const isChef = useCanManage();
  // Zusatzstunden der EIGENEN, bereits gespeicherten Schicht ändert nur der
  // Chef (Server-Regel seit 11.09.2026) — Feld hier nur zur Ansicht.
  const bonusLocked = !!shift && shift.userId === me.id && !isChef;
  const allLocations = useLocationsWithTemplates();

  // Standort-Filter: zeigt nur Orte, die dem User freigeschaltet sind
  // (leere allowedLocationIds = überall erlaubt). Ist der Standort einer
  // existierenden Schicht nicht mehr erlaubt, wird er trotzdem mit angezeigt,
  // damit der User bestehende Datensätze noch bearbeiten kann.
  // v1.3.0: der Chef ist automatisch an JEDEM Standort seiner Firma
  // einsetzbar — die alte Freischaltungs-Liste gilt für ihn nicht.
  const locations = useMemo(() => {
    if (!allLocations) return undefined;
    const allowed = me.allowedLocationIds ?? [];
    if (isChef || allowed.length === 0) return allLocations;
    return allLocations.filter(
      (l) => allowed.includes(l.id) || l.id === shift?.locationId,
    );
  }, [allLocations, me.allowedLocationIds, shift?.locationId, isChef]);

  const createMutation = useCreateShift();
  const updateMutation = useUpdateShift();

  const [shiftDate, setShiftDate] = useState(shift?.shiftDate ?? defaultDate ?? todayISO());
  const [locationId, setLocationId] = useState<string>(shift?.locationId ?? '');
  const [templateId, setTemplateId] = useState<string | ''>(shift?.templateId ?? '');
  const [startTime, setStartTime] = useState(shift?.startTime ?? '08:00');
  const [endTime, setEndTime] = useState(shift?.endTime ?? '14:00');
  const [status, setStatus] = useState<ShiftStatus>(
    shift?.status ?? defaultStatusForDate(shift?.shiftDate ?? defaultDate ?? todayISO()),
  );
  const [note, setNote] = useState(shift?.note ?? '');
  // Zusatzstunden (z. B. Fahrzeit, Aushilfe) — als String fürs Input,
  // beim Speichern in Number umgewandelt.
  const [bonusHoursInput, setBonusHoursInput] = useState<string>(
    shift?.bonusHours ? String(shift.bonusHours).replace('.', ',') : '0',
  );
  // Open-End-Marker: kommt entweder von der Schicht oder vom gewählten Template
  const [isOpenEnd, setIsOpenEnd] = useState<boolean>(shift?.isOpenEnd ?? false);
  const [error, setError] = useState<string | null>(null);
  // Beim Bearbeiten alles aufgeklappt, damit vorhandene Werte sichtbar sind;
  // beim Neuanlegen bleibt der Standardfall kurz.
  const [showMore, setShowMore] = useState<boolean>(
    !!shift && (!!shift.note || (shift.bonusHours ?? 0) > 0 || shift.status === 'cancelled'),
  );

  // Standort-Default beim ersten Laden: erster AKTIVER erlaubter Standort.
  useEffect(() => {
    if (locationId || !locations?.length) return;
    const firstActive = locations.find((l) => l.isActive) ?? locations[0];
    if (firstActive) setLocationId(firstActive.id);
  }, [locations, locationId]);

  const selectedLocation = useMemo<LocalLocationWithTemplates | undefined>(
    () => locations?.find((l) => l.id === locationId),
    [locations, locationId],
  );

  const availableTemplates = useMemo<LocalTemplate[]>(
    () => (selectedLocation?.templates ?? []).filter((t) => t.isActive),
    [selectedLocation],
  );

  // Bei Standort-Wechsel: ungültig gewordene templateId entfernen
  useEffect(() => {
    if (templateId && !availableTemplates.some((t) => t.id === templateId)) {
      setTemplateId('');
    }
  }, [availableTemplates, templateId]);

  function applyTemplate(id: string) {
    setTemplateId(id);
    if (!id) {
      // Vorlage entfernt → Schicht ist nicht mehr open-end
      setIsOpenEnd(false);
      return;
    }
    const tpl = availableTemplates.find((t) => t.id === id);
    if (tpl) {
      setStartTime(tpl.startTime);
      setEndTime(tpl.endTime);
      setIsOpenEnd(tpl.isOpenEnd);
    }
  }

  function onShiftDateChange(d: string) {
    setShiftDate(d);
    setStatus(defaultStatusForDate(d));
  }

  // --- Live-Berechnung ---
  const parsedBonus = (() => {
    const n = Number(bonusHoursInput.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();
  // Gesamtprüfung 11.09.2026: Pausen-Abzug wie in der Übersicht — bekannt
  // ist die Einstellung nur für die eigene Person.
  const previewOpts = { breakDeduction: (shift?.userId ?? me.id) === me.id && me.breakDeductionEnabled };
  const liveHours = shiftHours({ startTime, endTime, bonusHours: parsedBonus, isOpenEnd }, previewOpts);
  // Phase G (Entscheidung #6): bei einer FREMDEN Schicht ohne Lohn-Sichtbarkeit
  // ist shift.hourlyWage `null` — dann NICHT auf den eigenen Lohn (me.hourlyWage)
  // ausweichen (das würde einen falschen Wert vortäuschen), sondern verbergen.
  const hourlyWageForCalc: number | null = shift ? shift.hourlyWage : me.hourlyWage;
  const liveWage =
    hourlyWageForCalc == null
      ? null
      : shiftWage(
          {
            shiftDate,
            startTime,
            endTime,
            bonusHours: parsedBonus,
            isOpenEnd,
            hourlyWage: hourlyWageForCalc,
            status,
          },
          previewOpts,
        );
  // Bei Open-End-Schichten: Endzeit fehlt, wenn sie noch == startTime ist
  const openEndPending = isOpenEnd && startTime === endTime;

  const busy = createMutation.isPending || updateMutation.isPending;
  const loadingLocations = locations === undefined;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!locationId) {
      setError('Bitte einen Standort wählen.');
      hapticError();
      return;
    }
    // Open-End-Schichten dürfen Start==Ende haben (Endzeit folgt später).
    if (!isOpenEnd && startTime === endTime) {
      setError('Start- und End-Zeit dürfen nicht identisch sein.');
      hapticError();
      return;
    }
    const bonusNumeric = Number(bonusHoursInput.replace(',', '.'));
    if (!Number.isFinite(bonusNumeric) || bonusNumeric < 0 || bonusNumeric > 24) {
      setError('Zusatzstunden müssen zwischen 0 und 24 liegen.');
      hapticError();
      return;
    }
    try {
      let saved: LocalShift;
      if (shift) {
        saved = await updateMutation.mutateAsync({
          id: shift.id,
          input: {
            locationId,
            templateId: templateId || null,
            shiftDate,
            startTime,
            endTime,
            // 19.09.2026: den Status nur mitschicken, wenn er hier wirklich
            // geändert wurde. Sonst überschrieb ein Speichern mit einem alten
            // lokalen Stand („geplant“) das inzwischen automatisch gesetzte
            // „erledigt“ wieder.
            ...(status !== shift.status ? { status } : {}),
            note: note.trim() ? note.trim() : null,
            bonusHours: bonusNumeric,
            isOpenEnd,
          },
        });
      } else {
        saved = await createMutation.mutateAsync({
          locationId,
          templateId: templateId || null,
          shiftDate,
          startTime,
          endTime,
          status,
          note: note.trim() ? note.trim() : null,
          bonusHours: bonusNumeric,
          isOpenEnd,
        });
      }
      hapticSuccess();
      onSaved(saved);
    } catch (err) {
      hapticError();
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md lg:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto border border-border/60">
        <h3 className="text-lg font-semibold mb-4">
          {shift ? 'Schicht bearbeiten' : 'Schicht erfassen'}
        </h3>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Datum">
            <input
              required
              type="date"
              value={shiftDate}
              onChange={(e) => onShiftDateChange(e.target.value)}
              className="input"
            />
          </Field>

          <Field label="Standort">
            <select
              required
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="input"
            >
              <option value="" disabled>
                {loadingLocations ? '— lädt … —' : '— wählen —'}
              </option>
              {locations
                ?.filter((l) => l.isActive || l.id === locationId)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {!l.isActive ? ' (inaktiv)' : ''}
                  </option>
                ))}
            </select>
          </Field>

          {/* Vorlagen als Chips — ein Tap setzt Start/Ende */}
          {availableTemplates.length > 0 && (
            <div>
              <span className="block text-sm text-slate-300 mb-1.5">Vorlage</span>
              <div className="flex flex-wrap gap-2">
                <ChipButton active={!templateId} onClick={() => applyTemplate('')}>
                  freie Zeiten
                </ChipButton>
                {availableTemplates.map((t) => (
                  <ChipButton
                    key={t.id}
                    active={templateId === t.id}
                    onClick={() => applyTemplate(t.id)}
                    title={`${t.startTime}–${t.endTime}${t.isOpenEnd ? ' (offen)' : ''}`}
                  >
                    {t.name}
                  </ChipButton>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start">
              <input
                required
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="input"
              />
            </Field>
            <Field label={isOpenEnd ? 'Ende (optional)' : 'Ende'}>
              <input
                required={!isOpenEnd}
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="input"
              />
            </Field>
          </div>

          {openEndPending && (
            <p className="text-xs text-amber-300">
              Endzeit fehlt — Schicht zählt vorläufig mit 0 h. Trag sie ein, sobald du
              Feierabend hast.
            </p>
          )}

          {/* Live-Berechnung */}
          <div className="bg-slate-900/50 rounded p-3 text-sm space-y-0.5 border border-border/40">
            <div className="flex justify-between">
              <span className="text-slate-400">Schicht-Zeit</span>
              <span className="font-medium">
                {openEndPending
                  ? '— (offen)'
                  : `${shiftDurationHours({ startTime, endTime, isOpenEnd }).toFixed(2).replace('.', ',')} h`}
              </span>
            </div>
            {parsedBonus > 0 && (
              <div className="flex justify-between text-amber-300">
                <span>Zusatzstunden</span>
                <span className="font-medium">
                  +{parsedBonus.toFixed(2).replace('.', ',')} h
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-border/50 pt-1 mt-1">
              <span className="text-slate-400">Bezahlte Stunden</span>
              <span className="font-medium">{liveHours.toFixed(2).replace('.', ',')} h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Lohn (brutto)</span>
              <span className="font-medium text-emerald-400">{formatMoney(liveWage)}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Stundenlohn-Snapshot: {formatMoney(hourlyWageForCalc)}
            </p>
          </div>

          {/* Mehr Optionen: Status, Open-End, Zusatzstunden, Notiz */}
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="text-sm text-brand-light hover:text-brand !min-h-0 inline-flex items-center gap-1"
            aria-expanded={showMore}
          >
            <span>{showMore ? '▾' : '▸'}</span> Mehr Optionen
          </button>

          {showMore && (
            <div className="space-y-3 pt-1">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isOpenEnd}
                  onChange={(e) => setIsOpenEnd(e.target.checked)}
                  className="w-4 h-4 accent-brand"
                />
                Endzeit offen lassen (Mitarbeiter trägt nach)
              </label>

              <Field label="Status">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ShiftStatus)}
                  className="input"
                >
                  <option value="planned">Geplant</option>
                  <option value="done">Erledigt</option>
                  <option value="cancelled">Abgesagt</option>
                </select>
              </Field>

              <Field label="Zusatzstunden (z. B. Fahrzeit, Aushilfe)">
                <input
                  inputMode="decimal"
                  value={bonusHoursInput}
                  onChange={(e) => setBonusHoursInput(e.target.value)}
                  className={`input ${bonusLocked ? 'opacity-60' : ''}`}
                  placeholder="0"
                  aria-describedby="bonus-help"
                  readOnly={bonusLocked}
                />
                <p id="bonus-help" className="text-[11px] text-slate-500 mt-1">
                  {bonusLocked
                    ? 'Zusatzstunden deiner eigenen Schicht kann nur der Chef ändern.'
                    : shift
                    ? 'Wird zur Arbeitszeit addiert. Start/Ende der Schicht bleiben unverändert.'
                    : 'Wird zur Arbeitszeit addiert. Beim Selbst-Eintragen muss der Chef die Zusatzstunden bestätigen — lehnt er ab, zählen nur die gearbeiteten Stunden.'}
                </p>
              </Field>

              <Field label="Notiz">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="input resize-y"
                  placeholder="Optional"
                />
              </Field>
            </div>
          )}

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost w-full sm:w-auto"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || !locationId}
              className="btn w-full sm:w-auto"
            >
              {busy ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ChipButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={[
        'rounded-full px-3 py-1.5 text-sm !min-h-0 transition border',
        active
          ? 'bg-brand text-white border-brand'
          : 'bg-surface-3 text-slate-200 border-border hover:bg-slate-600',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}</span>
      {children}
    </label>
  );
}

function defaultStatusForDate(iso: string): ShiftStatus {
  const today = todayISO();
  return iso < today ? 'done' : 'planned';
}
